import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

export default function TermsPage() {
  const { t, i18n } = useTranslation();
  const { lang = 'en' } = useParams<{ lang: string }>();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      <div className="pt-20 md:pt-24 pb-4 bg-white border-b border-background-200/70">
        <div className="container-main">
          <nav className="flex items-center gap-2 text-sm text-foreground-500">
            <a href={`/${lang}/`} className="hover:text-primary-600 transition-colors">{t('breadcrumb.home')}</a>
            <i className="ri-arrow-right-s-line text-foreground-300"></i>
            <span className="text-foreground-700">Terms of Service</span>
          </nav>
        </div>
      </div>

      <section className="bg-white py-16 md:py-20">
        <div className="container-main text-center">
          <h1 className="font-heading text-3xl md:text-5xl font-bold text-foreground-900 mb-4">
            Terms of Service
          </h1>
          <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed text-base md:text-lg">
            Read the terms and conditions for using the AllChinaBuy Spreadsheet Resource Hub website and services. Last updated: August 25, 2026.
          </p>
        </div>
      </section>

      <section className="bg-background-50 py-12 md:py-16">
        <div className="container-main max-w-4xl">
          <div className="prose-content">
            <h2>Acceptance of Terms</h2>
            <p>
              By accessing and using allchinabuyspread.cyou (the "Website"), you accept and agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use this Website. These terms apply to all visitors, users, and others who access or use the Website.
            </p>

            <h2>About This Website</h2>
            <p>
              AllChinaBuy Spreadsheet Resource Hub is an independent informational resource website. We are not affiliated with, endorsed by, or the official representative of AllChinaBuy or any buying agent platform. We do not process orders, handle payments, or provide customer support for AllChinaBuy transactions. All product links redirect to third-party websites for actual purchasing.
            </p>

            <h2>Use of Content</h2>
            <p>
              All content on this Website, including text, images, guides, and product information, is provided for informational and educational purposes only. You may:
            </p>
            <p>
              - Access and read content for personal, non-commercial use
            </p>
            <p>
              - Share links to our pages on social media or other platforms
            </p>
            <p>
              - Quote small portions of text with proper attribution and a link back to the source page
            </p>
            <p>
              You may not:
            </p>
            <p>
              - Reproduce, copy, or redistribute our content in bulk without written permission
            </p>
            <p>
              - Use our content for commercial purposes without authorization
            </p>
            <p>
              - Modify or create derivative works from our content without permission
            </p>
            <p>
              - Use automated systems to scrape or harvest our content
            </p>

            <h2>Accuracy of Information</h2>
            <p>
              We make reasonable efforts to ensure the accuracy of information on this Website. However, we do not guarantee that all information is complete, accurate, reliable, or current. Product prices, availability, coupon codes, and shipping rates change frequently and may not reflect the latest data on the AllChinaBuy website. We encourage users to verify all information directly with AllChinaBuy before making purchase decisions.
            </p>
            <p>
              This Website and its content are provided "as is" without any warranties, express or implied. We disclaim all warranties, including but not limited to implied warranties of merchantability, fitness for a particular purpose, and non-infringement.
            </p>

            <h2>Third-Party Links</h2>
            <p>
              This Website contains links to third-party websites, including AllChinaBuy (kaspreadsheet.com). These links are provided for convenience and informational purposes. We are not responsible for the content, accuracy, or practices of any third-party websites. Visiting third-party websites is at your own risk, and you should review their respective terms of service and privacy policies.
            </p>

            <h2>User Conduct</h2>
            <p>
              When using this Website, you agree not to:
            </p>
            <p>
              - Use the Website for any unlawful purpose or in violation of any applicable laws
            </p>
            <p>
              - Attempt to interfere with the proper functioning of the Website
            </p>
            <p>
              - Use automated systems to access the Website in a manner that exceeds normal human browsing behavior
            </p>
            <p>
              - Upload or transmit any harmful code, malware, or viruses
            </p>
            <p>
              - Impersonate any person or entity or misrepresent your affiliation with any person or entity
            </p>

            <h2>Limitation of Liability</h2>
            <p>
              To the maximum extent permitted by law, AllChinaBuy Spreadsheet Resource Hub and its operators shall not be liable for any direct, indirect, incidental, special, consequential, or punitive damages arising from your use of this Website or reliance on any information contained herein. This includes but is not limited to damages for loss of profits, data, or other intangible losses.
            </p>
            <p>
              We are not responsible for any losses, damages, or disputes arising from transactions conducted on third-party websites linked from this Website. All purchasing decisions and transactions are solely between you and the respective third-party platform.
            </p>

            <h2>Changes to Terms</h2>
            <p>
              We reserve the right to modify or replace these Terms of Service at any time. Changes will be effective immediately upon posting to this page. Your continued use of the Website after any changes constitutes acceptance of the revised terms. We encourage you to review these terms periodically.
            </p>

            <h2>Governing Law</h2>
            <p>
              These Terms of Service shall be governed by and construed in accordance with the laws applicable to the jurisdiction where the Website operator is based, without regard to conflict of law principles. Any disputes arising from these terms shall be subject to the exclusive jurisdiction of the courts in that jurisdiction.
            </p>

            <h2>Contact Information</h2>
            <p>
              If you have any questions about these Terms of Service, please contact us through our <a href={`/${lang}/contact/`} className="text-primary-600 hover:underline">Contact page</a>.
            </p>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}